import React, { JSX } from 'react';

export const LIFECYCLE_REPORT_MESSAGE_MAX = 250;

import type { LifecycleFlagsState } from '../types/DeviceConfigureTypes';

export type { LifecycleFlagsState };

type SubmitLifecycleReportOperationProps = {
  selectedIds: string[];
  selectedLifecycleEventId: string;
  selectedLifecycleRaceId: string;
  events: { eventId: string; name?: string; status?: string }[];
  races: { raceId: string; name?: string }[];
  eventsLoading: boolean;
  racesLoading: boolean;
  flags: LifecycleFlagsState;
  lifecycleReportMessage: string;
  submitting: boolean;
  prefillLoading?: boolean;
  prefillSummary?: string;
  mixedSelection?: boolean;
  onSelectedLifecycleEventIdChange: (eventId: string) => void;
  onSelectedLifecycleRaceIdChange: (raceId: string) => void;
  onFlagsChange: (next: LifecycleFlagsState) => void;
  onLifecycleReportMessageChange: (message: string) => void;
  canEditAttributes?: boolean;
  attrRaceCount?: string;
  attrWaterproofedBy?: string;
  onAttrRaceCountChange?: (value: string) => void;
  onAttrWaterproofedByChange?: (value: string) => void;
};

function FlagRow(props: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}): JSX.Element {
  const { label, checked, disabled, onChange } = props;
  return (
    <label className="flex items-center gap-2 text-[11px] text-gray-800 dark:text-slate-200 cursor-pointer select-none">
      <input
        type="checkbox"
        className="h-3.5 w-3.5 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-200"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

export default function SubmitLifecycleReportOperation(
  props: SubmitLifecycleReportOperationProps,
): JSX.Element {
  const {
    selectedIds,
    selectedLifecycleEventId,
    selectedLifecycleRaceId,
    events,
    races,
    eventsLoading,
    racesLoading,
    flags,
    lifecycleReportMessage,
    submitting,
    prefillLoading = false,
    prefillSummary = '',
    mixedSelection = false,
    onSelectedLifecycleEventIdChange,
    onSelectedLifecycleRaceIdChange,
    onFlagsChange,
    onLifecycleReportMessageChange,
    canEditAttributes = false,
    attrRaceCount = '',
    attrWaterproofedBy = '',
    onAttrRaceCountChange,
    onAttrWaterproofedByChange,
  } = props;

  return (
    <div className="border border-gray-100 dark:border-slate-700 rounded-lg p-3">
      <h3 className="text-xs font-semibold text-gray-900 dark:text-slate-100 mb-2">Submit lifecycle report</h3>
      <p className="text-[11px] text-gray-500 dark:text-slate-400 mb-3">
        Set one or more lifecycle flags for each selected device. Clearing all flags marks the device as in service.
        Include a short note (required) for the audit trail.
      </p>
      {prefillLoading ? (
        <div className="mb-2 text-[11px] text-gray-500 dark:text-slate-400">Loading current lifecycle state…</div>
      ) : null}
      {!prefillLoading && prefillSummary ? (
        <div className="mb-2 text-[11px] text-gray-500 dark:text-slate-400">{prefillSummary}</div>
      ) : null}
      {!prefillLoading && mixedSelection ? (
        <div className="mb-2 text-[11px] text-amber-700 dark:text-amber-300">
          Selected devices have mixed current lifecycle values; the form is prefilled from the first selected device.
        </div>
      ) : null}
      <label className="block text-[11px] text-gray-700 dark:text-slate-300 mb-1 mt-3">Event (optional)</label>
      <select
        className="w-full rounded-md border border-gray-300 dark:border-slate-600 px-2 py-1 text-[11px] bg-white dark:bg-slate-900 disabled:opacity-60"
        value={selectedLifecycleEventId}
        disabled={eventsLoading || submitting}
        onChange={(e) => onSelectedLifecycleEventIdChange(e.target.value)}
      >
        <option value="">No event</option>
        {events.map((ev) => (
          <option key={ev.eventId} value={ev.eventId}>
            {ev.name ? `${ev.name} (${ev.eventId})` : ev.eventId}
          </option>
        ))}
      </select>
      {eventsLoading ? (
        <div className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">Loading events...</div>
      ) : null}

      <label className="block text-[11px] text-gray-700 dark:text-slate-300 mb-1 mt-3">Race (optional)</label>
      <select
        className="w-full rounded-md border border-gray-300 dark:border-slate-600 px-2 py-1 text-[11px] bg-white dark:bg-slate-900 disabled:opacity-60"
        value={selectedLifecycleRaceId}
        disabled={!selectedLifecycleEventId || racesLoading || submitting}
        onChange={(e) => onSelectedLifecycleRaceIdChange(e.target.value)}
      >
        <option value="">{selectedLifecycleEventId ? 'No race (event-level)' : 'Select an event first'}</option>
        {races.map((r) => (
          <option key={r.raceId} value={r.raceId}>
            {r.name ? `${r.name} (${r.raceId})` : r.raceId}
          </option>
        ))}
      </select>
      {selectedLifecycleEventId && racesLoading ? (
        <div className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">Loading races...</div>
      ) : null}

      <label className="mt-4 block text-[11px] font-semibold text-gray-700 dark:text-slate-300">
        Lifecycle flags
      </label>
      <p className="mb-2 text-[10px] text-gray-500 dark:text-slate-400">
        Independent of event/race — clear all to mark in service.
      </p>
      <div className="space-y-2 rounded-md border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800/80 px-2 py-2">
        <FlagRow
          label="Lost"
          checked={flags.lost}
          disabled={submitting}
          onChange={(lost) => onFlagsChange({ ...flags, lost })}
        />
        <FlagRow
          label="Decommissioned"
          checked={flags.decommissioned}
          disabled={submitting}
          onChange={(decommissioned) => onFlagsChange({ ...flags, decommissioned })}
        />
        <FlagRow
          label="Damaged"
          checked={flags.damaged}
          disabled={submitting}
          onChange={(damaged) => onFlagsChange({ ...flags, damaged })}
        />
        <FlagRow
          label="Errored"
          checked={flags.errored}
          disabled={submitting}
          onChange={(errored) => onFlagsChange({ ...flags, errored })}
        />
        <button
          type="button"
          className="mt-1 text-[11px] text-blue-700 hover:underline disabled:opacity-50 dark:text-blue-300"
          disabled={submitting}
          onClick={() => onFlagsChange({ lost: false, decommissioned: false, damaged: false, errored: false })}
        >
          Clear all (in service)
        </button>
      </div>
      <label className="block text-[11px] text-gray-700 dark:text-slate-300 mb-1 mt-3">Message</label>
      <textarea
        className="w-full min-h-[120px] rounded-md border border-gray-300 dark:border-slate-600 px-2 py-1 text-[11px] bg-white dark:bg-slate-900"
        value={lifecycleReportMessage}
        maxLength={LIFECYCLE_REPORT_MESSAGE_MAX}
        onChange={(e) => onLifecycleReportMessageChange(e.target.value)}
        placeholder="Reason or context for this lifecycle change..."
        disabled={submitting}
      />
      <div className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">
        {lifecycleReportMessage.length}/{LIFECYCLE_REPORT_MESSAGE_MAX}
      </div>

      {canEditAttributes ? (
        <div className="mt-4 rounded-md border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800/80 px-2 py-2">
          <label className="block text-[11px] font-semibold text-gray-700 dark:text-slate-300">
            Attributes (optional)
          </label>
          <p className="mb-2 text-[10px] text-gray-500 dark:text-slate-400">
            Leave blank to keep current values. Applies to all selected devices.
          </p>
          <label className="mt-1 block text-[11px] text-gray-700 dark:text-slate-300 mb-1">Race count</label>
          <input
            type="number"
            min={0}
            step={1}
            className="w-full rounded-md border border-gray-300 dark:border-slate-600 px-2 py-1 text-[11px] bg-white dark:bg-slate-900 disabled:opacity-60"
            value={attrRaceCount}
            disabled={submitting}
            onChange={(e) => onAttrRaceCountChange?.(e.target.value)}
            placeholder="e.g. 12"
          />
          <label className="mt-2 block text-[11px] text-gray-700 dark:text-slate-300 mb-1">Waterproofed by</label>
          <input
            type="text"
            className="w-full rounded-md border border-gray-300 dark:border-slate-600 px-2 py-1 text-[11px] bg-white dark:bg-slate-900 disabled:opacity-60"
            value={attrWaterproofedBy}
            disabled={submitting}
            onChange={(e) => onAttrWaterproofedByChange?.(e.target.value)}
            placeholder="e.g. James"
          />
        </div>
      ) : null}

      <div className="mt-3 border border-gray-100 dark:border-slate-700 rounded-md p-2 bg-gray-50 dark:bg-slate-800">
        <div className="text-[11px] text-gray-800 dark:text-slate-200 font-medium mb-1">
          Selected devices ({selectedIds.length})
        </div>
        <div className="max-h-28 overflow-auto">
          {selectedIds.length > 0 ? (
            selectedIds.map((id) => (
              <div key={id} className="font-mono text-[11px] break-all text-gray-800 dark:text-slate-200">
                {id}
              </div>
            ))
          ) : (
            <div className="text-[11px] text-gray-400 dark:text-slate-400">No devices selected.</div>
          )}
        </div>
      </div>
    </div>
  );
}
